import AuthoringCoPilotApi from "@apis/authoring.copilot.api";
import { KnowledgeBaseContentType } from "@plugins/authoring-copilot/types.ts";
import { UploadFlow, FileStatus } from "@plugins/file-uploader-v2/types.ts";
import {
  uploadFileToS3,
  cleanFileName,
} from "@plugins/file-uploader-v2/composables/useFileUploader.ts";
import type { RichTextContext } from "@plugins/richtext/types.ts";
import MediaObjectService from "@plugins/richtext/service/media.object.service.ts";
import { RESOURCE_TYPE } from "@plugins/common/constant/resource.constant";

export interface PdfCaptureServiceContext {
  resourceType: string;
  resourceId: number;
  richTextContext: RichTextContext;
}

export interface PdfCaptureSaveParams {
  dataUrl: string;
  filename: string;
  indexNo: number;
}

export class PdfCaptureService {
  private context: PdfCaptureServiceContext;
  private mediaObjectService: MediaObjectService;

  constructor(context: PdfCaptureServiceContext) {
    this.context = context;
    // For PDF capture, the media object always belongs to the lesson context.
    // Override resourceUuid, resourceType and uploadFrom explicitly so
    // MediaObjectService.create() sends the correct contextUuid/contextType
    // regardless of what the parent component has set on the richTextContext.
    this.mediaObjectService = new MediaObjectService({
      ...context.richTextContext,
      resourceUuid: context.richTextContext.lessonUuid,
      resourceType: RESOURCE_TYPE.LESSON,
      uploadFrom: "lesson",
    });
  }

  async captureAndSave(
    params: PdfCaptureSaveParams,
  ): Promise<{ mediaObjectUuid: string; kbEntry: unknown }> {
    // 1. Convert data URL to Blob
    const blob = this.dataUrlToBlob(params.dataUrl);
    const file = new File([blob], cleanFileName(params.filename), { type: "image/jpeg" });

    // 2–4. Generate URL → upload to S3 → poll virus scan
    const uploadResult = await uploadFileToS3(file, UploadFlow.IMG);

    if (uploadResult.status !== FileStatus.READY || !uploadResult.uuid || !uploadResult.name) {
      throw new Error("File upload or virus scan failed");
    }

    // 5. Create media object via MediaObjectService
    const mediaObjectData = (await this.mediaObjectService.create(uploadResult)) as {
      uuid: string;
      title?: string;
    };

    if (!mediaObjectData?.uuid) {
      throw new Error("Failed to create media object");
    }

    const mediaObjectUuid: string = mediaObjectData.uuid;
    // Fall back to the cleaned filename so the title is consistent with what the
    // backend stores regardless of whether the API response includes a title field.
    const mediaTitle: string = (mediaObjectData.title ?? cleanFileName(params.filename)).replace(
      /\.jpg$/i,
      "",
    );

    // 6. Create KB entry
    const kbResponse = await AuthoringCoPilotApi.createKnowledgeBase({
      resourceType: this.context.resourceType,
      resourceId: this.context.resourceId,
      contentType: KnowledgeBaseContentType.MEDIA,
      contentId: mediaObjectUuid,
      indexNo: params.indexNo,
    });

    if (kbResponse?.code !== "ok") {
      throw new Error("Failed to create knowledge base entry");
    }

    return {
      mediaObjectUuid,
      kbEntry: {
        ...(kbResponse.data ?? {}),
        contentId: mediaObjectUuid,
        mediaTitle,
        mediaType: "IMAGE",
      },
    };
  }

  private dataUrlToBlob(dataUrl: string): Blob {
    const [header, data] = dataUrl.split(",");
    const mimeMatch = header.match(/:(.*?);/);
    if (!mimeMatch) {
      throw new Error("Invalid data URL format");
    }
    const mime = mimeMatch[1];
    const binary = atob(data);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    return new Blob([array], { type: mime });
  }
}
